import React, { useEffect, useState } from 'react';
import { BudgetSummaryDTO } from './project.type';
import { Card, Divider, Grid, IconButton, Tooltip } from '@mui/material';
import { Typography, Box } from 'components/index';
import { format, formatPercentage } from '../../common/utils/common.util';
import { NEGATIVE_NUMBER_COLOR } from '../../common/constants/general';
import { AttachMoney, Sync } from '@mui/icons-material';
import WarningIcon from '@mui/icons-material/Warning';
import CustomizedTextField from './CustomizedTextField';
import NumericFormatInput from '../general/NumericFormatInput';

// Fields that must always show cents rather than being rounded to the nearest dollar
const CENTS_LABELS = new Set(['Requested Amount', 'Total Amount Recommended for Funding', 'Difference', 'Adjustments by CDCC']);

// Non-zero Difference styling: intentional in many payapps, so it reads as an alert, not an error.
const DIFFERENCE_ALERT_COLOR = '#D32F2F';
const DIFFERENCE_ALERT_BG = '#FDE7E7';

// Helper function to determine if a field should be rounded to dollar
const shouldRoundToDollar = (label: string): boolean => {
  // % fields should not be rounded to dollar, nor should fields that must always show cents
  return !label.includes('%') && !CENTS_LABELS.has(label);
};

/** [label, value, isPrimary, formatType] */
type BudgetRow = [string, any, boolean, string];

type ProjectBudgetProps = {
  budgetSummary?: BudgetSummaryDTO;
  drawBudget?: BudgetSummaryDTO;
  errorFieldNames: string[];
  isEditMode?: boolean;
  onEditChange?: (field: string, value: any, target: 'drawSummary' | 'budgetSummary') => void;
  previousFundingsTotal?: number;
  /** Renders every row in one column (mirrors the Excel G702/3 layout) instead of the default 2-column split. */
  singleColumn?: boolean;
  /** Stretches the card to the height of its container, for side-by-side layouts. */
  fullHeight?: boolean;
};

const Row: React.FC<{
  label: string;
  value: any;
  index: number;
  isPrimary: boolean;
  formatType: string;
  isHighlighted?: boolean;
  isEditMode?: boolean;
  onEditChange?: (field: string, value: any) => void;
  fieldName?: string;
  hasError?: boolean;
  budgetSummary?: BudgetSummaryDTO;
}> = ({
  label,
  value,
  index,
  isPrimary,
  formatType,
  isHighlighted = false,
  isEditMode,
  onEditChange,
  fieldName,
  hasError,
  budgetSummary,
}) => {
  // Normalize value: handle null, undefined, '-', or convert to string
  let normalizedValue = value;
  if (
    normalizedValue === null ||
    normalizedValue === undefined ||
    normalizedValue === '-' ||
    normalizedValue === 'null' ||
    normalizedValue === 'undefined'
  ) {
    normalizedValue = 0;
  }

  // Apply rounding logic for currency fields before formatting
  let valueToFormat = normalizedValue;
  if (formatType === 'currency' && typeof normalizedValue === 'number') {
    if (shouldRoundToDollar(label)) {
      valueToFormat = Math.round(normalizedValue);
    }
    // If not rounding, keep original value (formatBudget will handle 2 decimals)
  }

  // Check negative on original value to preserve sign for display
  const isNegativeNumber = typeof normalizedValue === 'number' && parseFloat(normalizedValue.toString()) < 0;

  // A non-zero Difference is not an error (it is often intentional), but reviewers must not miss it.
  // Keyed off the rounded value that is actually displayed, so a sub-cent/sub-dollar difference
  // rendering as 0 does not get flagged.
  const isDifferenceAlert = label === 'Difference' && typeof valueToFormat === 'number' && Math.abs(valueToFormat) >= 0.01;
  const formattedValue = format(
    valueToFormat?.toString() || '0',
    formatType,
    formatType !== 'currency',
    formatType === 'currency' && CENTS_LABELS.has(label),
  );

  // Local draft so keystrokes update the field instantly without pushing into the shared
  // project state (and re-rendering the whole page, incl. the line-items table) on every keystroke.
  // Only committed via onEditChange on blur. Re-synced whenever the underlying value changes
  // externally (draw switch, Cancel, the auto-adjust/refresh buttons below).
  const [draft, setDraft] = useState(normalizedValue);
  useEffect(() => {
    setDraft(normalizedValue);
  }, [normalizedValue]);

  const handleAutoAdjust = () => {
    if (!onEditChange || !budgetSummary) return;

    const requestedAmount = budgetSummary.requestedAmount || 0;
    const currentPaymentDue = budgetSummary.currentPaymentDue || 0;
    const difference = requestedAmount - currentPaymentDue;

    onEditChange('adjustmentsByCDCC', difference);
  };

  if (isEditMode && onEditChange && fieldName) {
    return (
      <Box display='flex' justifyContent='space-between' alignItems='center' px={2} py={0.5}>
        <Typography variant='button' fontWeight='medium' gutterBottom color={isPrimary ? 'primary' : undefined}>
          {isHighlighted && <WarningIcon color='warning' sx={{ fontSize: '0.9rem !important', mb: 0.3, mr: 0.5 }} />}
          {label}
          {fieldName === 'adjustmentsByCDCC' && (
            <Tooltip title='Auto-adjust to make difference zero' placement='top' arrow={false}>
              <IconButton size='small' onClick={handleAutoAdjust} sx={{ ml: 0.5, p: 0.5 }} color='primary'>
                <Sync fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
        <CustomizedTextField
          placeholder='0'
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={() => onEditChange(fieldName, draft)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              (e.target as HTMLInputElement).blur();
            }
          }}
          InputProps={{
            inputComponent: NumericFormatInput as any,
          }}
          inputProps={{
            style: { textAlign: 'right' },
          }}
          hasError={hasError}
          sx={{ width: '50%' }}
        />
      </Box>
    );
  }

  return (
    <Box
      display='flex'
      justifyContent='space-between'
      alignItems='center'
      px={2}
      py={1}
      sx={{
        '&:hover': {
          backgroundColor: isDifferenceAlert ? DIFFERENCE_ALERT_BG : '#eaf5f7',
        },
        borderRadius: '8px',
        ...(isDifferenceAlert
          ? {
              backgroundColor: DIFFERENCE_ALERT_BG,
              border: `1px solid ${DIFFERENCE_ALERT_COLOR}`,
              borderLeft: `6px solid ${DIFFERENCE_ALERT_COLOR}`,
              boxShadow: `0 0 0 3px ${DIFFERENCE_ALERT_COLOR}22`,
            }
          : {}),
      }}
    >
      <Typography
        variant='button'
        fontWeight={isDifferenceAlert ? 700 : 'medium'}
        gutterBottom
        color={isPrimary ? 'primary' : undefined}
        sx={isDifferenceAlert ? { color: DIFFERENCE_ALERT_COLOR } : undefined}
      >
        {isHighlighted && <WarningIcon color='warning' sx={{ fontSize: '0.9rem !important', mb: 0.3, mr: 0.5 }} />}
        {isDifferenceAlert && (
          <Tooltip title='Requested Amount does not match the Total Amount Recommended for Funding' placement='top' arrow={false}>
            <WarningIcon sx={{ fontSize: '1rem !important', mb: 0.3, mr: 0.5, color: DIFFERENCE_ALERT_COLOR }} />
          </Tooltip>
        )}
        {label}
        {fieldName === 'adjustmentsByCDCC' && (
          <Tooltip
            title={!isEditMode || !onEditChange ? 'Switch to edit mode to enable' : 'Auto-adjust to make difference zero'}
            placement='top'
            arrow={false}
          >
            <span>
              <IconButton
                size='small'
                onClick={handleAutoAdjust}
                sx={{ ml: 0.5, p: 0.5 }}
                color='primary'
                disabled={!isEditMode || !onEditChange}
              >
                <Sync fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Typography>
      <Typography
        variant='button'
        sx={{
          color: isDifferenceAlert ? DIFFERENCE_ALERT_COLOR : isNegativeNumber ? NEGATIVE_NUMBER_COLOR : 'inherit',
          fontWeight: isDifferenceAlert ? 700 : isPrimary ? 500 : 0,
          ...(isDifferenceAlert ? { fontSize: '1rem', letterSpacing: '0.02em' } : {}),
        }}
      >
        {formattedValue}
      </Typography>
    </Box>
  );
};

const labelToFieldMap: Partial<Record<string, keyof BudgetSummaryDTO>> = {
  'Adjustments by CDCC': 'adjustmentsByCDCC',
  'Requested Amount': 'requestedAmount',
  'Est. Retainage Released This Period': 'estRetainageReleasedThisPeriod',
};

const ProjectBudget: React.FC<ProjectBudgetProps> = ({
  budgetSummary,
  drawBudget,
  errorFieldNames,
  isEditMode,
  onEditChange,
  previousFundingsTotal,
  singleColumn = false,
  fullHeight = false,
}) => {
  const handleEditChange = (field: string, value: any) => {
    onEditChange && onEditChange(field, value, 'budgetSummary');
  };

  if (!budgetSummary) return null;

  const firstColumnRows: BudgetRow[] = [
    ['Original Contract Budget Sum', budgetSummary.originalContractBudgetSum, true, 'currency'],
    ['Reallocations / Change Orders', budgetSummary.reallocationsChangeOrders, false, 'currency'],
    ['Contract Sum to Date', budgetSummary.contractBudgetSumToDate, true, 'currency'],
    ['Total Completed and Stored to Date', budgetSummary.totalCompletedAndStored, false, 'currency'],
    ['Total Retainage Held', budgetSummary.totalRetainageHeld, false, 'currency'],
    ['% Retainage', formatPercentage(budgetSummary.totalRetainageHeldPercentage.toString()), false, 'percentage'],
    ['Total Earned Less Retainage', budgetSummary.totalEarnedLessRetainage, false, 'currency'],
    ['Less Previous Fundings', previousFundingsTotal ?? budgetSummary.lessPreviousFundings, false, 'currency'],
  ];

  const secondColumnRows: BudgetRow[] = [
    ['Current Payment Due', budgetSummary.currentPaymentDue, false, 'currency'],
    ['Balance to Complete Plus Retainage', budgetSummary.balanceToCompletePlusRetainage, false, 'currency'],
    ['Adjustments by CDCC', budgetSummary.adjustmentsByCDCC, false, 'currency'],
    ['Total Amount Recommended for Funding', budgetSummary.totalAmountReccForFunding, true, 'currency'],
    ['Requested Amount', budgetSummary.requestedAmount, false, 'currency'],
    ['Difference', budgetSummary.difference, false, 'currency'],
    ['Est. Retainage Released This Period', budgetSummary.estRetainageReleasedThisPeriod, false, 'currency'],
    ['Total Contract / Budget % Complete', formatPercentage(budgetSummary.totalContractBudgetPerComplete.toString()), false, 'percentage'],
  ];

  // Single column keeps the Excel reading order: the two columns simply run one after the other.
  const columns: BudgetRow[][] = singleColumn ? [[...firstColumnRows, ...secondColumnRows]] : [firstColumnRows, secondColumnRows];

  return (
    <div style={fullHeight ? { display: 'flex', flex: 1, width: '100%' } : undefined}>
      <Card elevation={3} style={{ padding: '16px', flex: 1, ...(fullHeight ? { width: '100%' } : {}) }}>
        <Box display='flex' alignItems='center' justifyContent={'space-between'}>
          <Box display='flex' alignItems='center'>
            <AttachMoney color='primary' sx={{ fontSize: '24px !important', fontWeight: '700 !important' }} />
            <Typography variant='h5' color='primary' sx={{ ml: 0.5, mb: 0 }}>
              G702/3 Summary
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'stretch' }}>
          {columns.map((rows, colIndex) => (
            <Grid key={colIndex} size={singleColumn ? 12 : { xl: 6 }} sx={{ display: 'flex' }} flexGrow={1}>
              <Card elevation={!singleColumn && colIndex === 1 ? 3 : 1} style={{ padding: '16px', flex: 1 }}>
                {rows.map(([label, value, isPrimary, formatType], index) => {
                  const fieldName = labelToFieldMap[label];
                  const isEditable = isEditMode && !!fieldName;

                  return (
                    <React.Fragment key={label}>
                      <Row
                        label={label}
                        value={isEditable ? drawBudget?.[fieldName!] : value}
                        index={index}
                        isPrimary={isPrimary}
                        formatType={formatType}
                        isHighlighted={errorFieldNames.includes(label)}
                        isEditMode={isEditable}
                        onEditChange={handleEditChange}
                        fieldName={fieldName}
                        hasError={errorFieldNames.includes(label)}
                        budgetSummary={budgetSummary}
                      />
                      {index < rows.length - 1 ? <Divider sx={{ margin: 0 }} /> : null}
                    </React.Fragment>
                  );
                })}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Card>
    </div>
  );
};

export default ProjectBudget;
